import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  Boxes,
  Package,
  History,
  Search,
  Trash2,
} from 'lucide-react';

export const AdminInventory = () => {
  const [activeTab, setActiveTab] = useState('products'); // 'products' | 'movements'
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { success, error: toastError } = useToast();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.products({ search, page });
      if (res && res.data) {
        setProducts(res.data.data || []);
        setPagination({
          currentPage: res.data.current_page,
          lastPage: res.data.last_page,
          total: res.data.total,
          from: res.data.from,
          to: res.data.to,
        });
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar inventario global.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toastError]);

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.movements({ page });
      if (res && res.data) {
        setMovements(res.data.data || []);
        setPagination({
          currentPage: res.data.current_page,
          lastPage: res.data.last_page,
          total: res.data.total,
          from: res.data.from,
          to: res.data.to,
        });
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar movimientos.');
    } finally {
      setLoading(false);
    }
  }, [page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'products') {
        fetchProducts();
      } else {
        fetchMovements();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [activeTab, fetchProducts, fetchMovements]);

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;
    setDeleting(true);
    try {
      await adminService.deleteProduct(productToDelete.id);
      success('Producto eliminado del sistema.');
      setDeleteConfirmOpen(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar producto.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
          <Boxes className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
          Inventario Global & Auditoría Kardex
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
          Supervisión de productos en catálogo y registro histórico de entradas y salidas
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            setActiveTab('products');
            setPage(1);
          }}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'products'
              ? 'bg-[#08707A] text-white shadow-sm'
              : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800'
          }`}
        >
          <Package className="w-4 h-4" />
          Catálogo Global de Productos
        </button>

        <button
          onClick={() => {
            setActiveTab('movements');
            setPage(1);
          }}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'movements'
              ? 'bg-[#08707A] text-white shadow-sm'
              : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800'
          }`}
        >
          <History className="w-4 h-4" />
          Auditoría de Movimientos de Stock
        </button>
      </div>

      {/* Main Content Card */}
      <Card>
        {activeTab === 'products' && (
          <CardHeader>
            <div className="w-full sm:w-80">
              <Input
                placeholder="Buscar por producto o SKU..."
                icon={Search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="py-1.5"
              />
            </div>
          </CardHeader>
        )}

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando información..." className="py-12" />
          ) : activeTab === 'products' ? (
            products.length === 0 ? (
              <EmptyState title="No hay productos registrados" description="El catálogo global está vacío." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                    <tr>
                      <th className="py-3 px-4">Producto & SKU</th>
                      <th className="py-3 px-4">Propietario (Usuario)</th>
                      <th className="py-3 px-4">Precio Venta</th>
                      <th className="py-3 px-4 text-center">Stock Actual</th>
                      <th className="py-3 px-4 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {products.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-slate-100">
                          {p.name}
                          {p.sku && <span className="ml-2 font-mono text-[10px] text-slate-400">SKU: {p.sku}</span>}
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-800 dark:text-slate-200">{p.user?.name || '—'}</div>
                          <div className="text-[11px] text-slate-400">{p.user?.email}</div>
                        </td>
                        <td className="py-3.5 px-4 font-bold text-slate-800 dark:text-slate-200">
                          ${Number(p.price).toFixed(2)}
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold">
                          {p.stock} unid.
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setProductToDelete(p);
                              setDeleteConfirmOpen(true);
                            }}
                            title="Eliminar producto"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            movements.length === 0 ? (
              <EmptyState title="No hay movimientos" description="Sin registros de kardex en el sistema." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                    <tr>
                      <th className="py-3 px-4">Fecha & Hora</th>
                      <th className="py-3 px-4">Producto</th>
                      <th className="py-3 px-4">Usuario</th>
                      <th className="py-3 px-4">Tipo & Motivo</th>
                      <th className="py-3 px-4 text-right">Variación Cantidad</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {movements.map((m) => {
                      const isPositive = Number(m.quantity) > 0;
                      return (
                        <tr key={m.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                          <td className="py-3.5 px-4 text-slate-500">
                            {new Date(m.created_at).toLocaleString()}
                          </td>
                          <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-slate-100">
                            {m.product?.name || `Producto #${m.product_id}`}
                          </td>
                          <td className="py-3.5 px-4 text-slate-700 dark:text-slate-300">
                            {m.user?.name || '—'}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className="font-bold text-slate-800 dark:text-slate-200 uppercase text-[10px]">
                              {m.type}
                            </span>
                            {m.notes && <span className="ml-2 text-slate-500">· {m.notes}</span>}
                          </td>
                          <td className="py-3.5 px-4 text-right font-mono font-black text-sm">
                            <span className={isPositive ? 'text-emerald-600' : 'text-rose-600'}>
                              {isPositive ? `+${m.quantity}` : m.quantity}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteProduct}
        title="¿Eliminar producto global?"
        message={`¿Estás seguro de que deseas eliminar "${productToDelete?.name}"?`}
        confirmText="Eliminar"
        isLoading={deleting}
      />
    </div>
  );
};
